import {
  CALENDAR_MULTIPLE_POST_TITLE,
  CALENDAR_POST_TITLE,
  CALENDAR_REELS_TITLE,
  CALENDAR_STORIES_TITLE,
  CALENDAR_SOURCE_AUTOMATED,
  CALENDAR_SOURCE_MANUAL,
  EVENT_DETAILS_NETWORK_FACEBOOK,
  EVENT_DETAILS_NETWORK_INSTAGRAM,
  EVENT_DETAILS_NETWORK_LINKEDIN,
  EVENT_DETAILS_NETWORK_TIKTOK,
  EVENT_DETAILS_NETWORK_YOUTUBE,
  FACEBOOK as FACEBOOK_KEY,
  INSTAGRAM as INSTAGRAM_KEY,
  LINKEDIN as LINKEDIN_KEY,
  TIKTOK as TIKTOK_KEY,
  YOUTUBE as YOUTUBE_KEY,
} from "../i18n/keysTranslations";
import { resolveTimezone, toZonedDisplayDate } from "./timezone";
import {
  classifyCalendarPublishIssue,
  resolveCalendarEventStatus,
  STATUS_CONFIG,
} from "./calendarEventPublishStatus";
import {
  FACEBOOK,
  FACEBOOK_CONTENT_TYPE_REELS,
  FACEBOOK_CONTENT_TYPE_STORIES,
  INSTAGRAM,
  INSTAGRAM_CONTENT_TYPE_REELS,
  INSTAGRAM_CONTENT_TYPE_STORIES,
  LINKEDIN,
  META,
  STATES,
  TIKTOK,
  YOUTUBE,
} from "./constants";

export const CALENDAR_EVENT_DURATION_MS = 10 * 60 * 1000;

const DEFAULT_PLATFORM_KEY = META;

const PLATFORM_CONFIG = {
  [FACEBOOK]: {
    calendarLabelKey: FACEBOOK_KEY,
    networkLabelKey: EVENT_DETAILS_NETWORK_FACEBOOK,
    eventColor: "#3b82f6",
  },
  [TIKTOK]: {
    calendarLabelKey: TIKTOK_KEY,
    networkLabelKey: EVENT_DETAILS_NETWORK_TIKTOK,
    eventColor: "#000000",
  },
  [YOUTUBE]: {
    calendarLabelKey: YOUTUBE_KEY,
    networkLabelKey: EVENT_DETAILS_NETWORK_YOUTUBE,
    eventColor: "#FF0000",
  },
  [INSTAGRAM]: {
    calendarLabelKey: INSTAGRAM_KEY,
    networkLabelKey: EVENT_DETAILS_NETWORK_INSTAGRAM,
    eventColor: "#cc2366",
  },
  [LINKEDIN]: {
    calendarLabelKey: LINKEDIN_KEY,
    networkLabelKey: EVENT_DETAILS_NETWORK_LINKEDIN,
    eventColor: "#0077B5",
  },
};

const toTitleCase = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const getPlatformConfig = (platformKey) =>
  PLATFORM_CONFIG[platformKey] ?? PLATFORM_CONFIG[DEFAULT_PLATFORM_KEY];

export const getCalendarPlatformLabel = ({ platformKey, t, labelType }) => {
  if (!platformKey) {
    return "";
  }

  const config = PLATFORM_CONFIG[platformKey];
  if (!config) {
    return toTitleCase(platformKey);
  }

  if (config.displayName) {
    return config.displayName;
  }

  const translationKey =
    labelType === "network" ? config.networkLabelKey : config.calendarLabelKey;

  return translationKey ? t(translationKey) : toTitleCase(platformKey);
};

export const getCalendarPlatformEventColor = (platformKey) =>
  getPlatformConfig(platformKey).eventColor;

const getEventStatus = ({ statesByPlatform, t }) => {
  const state = resolveCalendarEventStatus(statesByPlatform);
  const { labelKey, opacity } =
    STATUS_CONFIG[state] ?? STATUS_CONFIG[STATES.SCHEDULED];

  return {
    state,
    label: t(labelKey),
    opacity,
  };
};

const getCalendarTitleKey = (platformsData) => {
  if (!platformsData || typeof platformsData !== "object") {
    return CALENDAR_POST_TITLE;
  }

  const platformKeys = Object.keys(platformsData);
  const singlePlatform = platformKeys[0];

  if (
    platformKeys.length !== 1 ||
    ![FACEBOOK, INSTAGRAM].includes(singlePlatform)
  ) {
    return CALENDAR_POST_TITLE;
  }

  const mediaType = platformsData[singlePlatform]?.mediaType;
  if (!mediaType) {
    return CALENDAR_POST_TITLE;
  }

  const contentTypeMap = {
    [INSTAGRAM_CONTENT_TYPE_STORIES]: CALENDAR_STORIES_TITLE,
    [INSTAGRAM_CONTENT_TYPE_REELS]: CALENDAR_REELS_TITLE,
    [FACEBOOK_CONTENT_TYPE_STORIES]: CALENDAR_STORIES_TITLE,
    [FACEBOOK_CONTENT_TYPE_REELS]: CALENDAR_REELS_TITLE,
  };

  return contentTypeMap[mediaType] || CALENDAR_POST_TITLE;
};

export const buildCalendarEvents = ({
  scheduledContents,
  t,
  automationNames = {},
}) => {
  const calendarEvents = [];

  Object.entries(scheduledContents || {}).forEach(
    ([fallbackId, scheduledPost]) => {
      const platformKeys = scheduledPost.platforms;
      if (
        !platformKeys ||
        !Array.isArray(platformKeys) ||
        platformKeys.length === 0
      ) {
        return;
      }

      const statesByPlatform = scheduledPost.states;
      const stateValues =
        statesByPlatform && typeof statesByPlatform === "object"
          ? Object.values(statesByPlatform).filter(Boolean)
          : [];
      const isOnlySkipped =
        stateValues.length > 0 &&
        stateValues.every((state) => state === STATES.SKIPPED);
      if (isOnlySkipped) {
        return;
      }

      const eventTimezone = resolveTimezone(scheduledPost.timezone);
      const startDate = toZonedDisplayDate({
        date: scheduledPost.scheduledDate,
        timezone: eventTimezone,
      });
      if (!startDate || Number.isNaN(startDate.getTime())) {
        return;
      }

      const endDate = new Date(
        startDate.getTime() + CALENDAR_EVENT_DURATION_MS
      );

      const isMultiPlatformPost = platformKeys.length > 1;
      const primaryPlatformKey = isMultiPlatformPost ? null : platformKeys[0];

      const publishIssue = classifyCalendarPublishIssue(statesByPlatform);
      const { label: statusLabel } = getEventStatus({ statesByPlatform, t });

      const calendarPlatformLabel = getCalendarPlatformLabel({
        platformKey: primaryPlatformKey,
        t,
        labelType: "calendar",
      });

      const calendarTitleKey = getCalendarTitleKey(scheduledPost.platformsData);

      const title = isMultiPlatformPost
        ? t(CALENDAR_MULTIPLE_POST_TITLE)
        : `${t(calendarTitleKey)} - ${calendarPlatformLabel}`;

      const networkLabel = isMultiPlatformPost
        ? t(CALENDAR_MULTIPLE_POST_TITLE)
        : getCalendarPlatformLabel({
            platformKey: primaryPlatformKey,
            t,
            labelType: "network",
          });

      const isAutomated = scheduledPost.source === "automation";

      calendarEvents.push({
        id: scheduledPost.scheduleID || fallbackId,
        title,
        start: startDate,
        end: endDate,
        timezone: eventTimezone,
        platform: primaryPlatformKey,
        platforms: platformKeys,
        platformsData: scheduledPost.platformsData || {},
        states: statesByPlatform,
        originalData: scheduledPost,
        isMultiple: isMultiPlatformPost,
        networkLabel,
        statusLabel,
        publishIssue,
        source: isAutomated ? "automation" : "manual",
        sourceLabel: t(
          isAutomated ? CALENDAR_SOURCE_AUTOMATED : CALENDAR_SOURCE_MANUAL
        ),
        automationID: scheduledPost.automationID || null,
        automationName: automationNames[scheduledPost.automationID] || "",
        contentID: scheduledPost.contentID || null,
      });
    }
  );

  return calendarEvents;
};
